import React, { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal, Table } from '@components/common';
import StopRentingAssetForm
	from '@components/routes/AdminPanel/ManageAcquiredAssets/StopRentingAssetForm/StopRentingAssetForm';

import { useMutateStopRentingAsset } from '@hooks/assets/useMutateStopRentingAsset';
import { useQueryAssetProfileOutgoingRequests } from '@hooks/assets/useQueryAssetProfileOutgoingRequests';

import { assetProfileOutgoingRequests } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setEndRentDate,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setStartRentDate,
} from '@slices/assetProfileOutgoingRequests';

import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
} from '@interfaces/components/table';

import { getColumns } from './manageOutgoingRequestsColumns';

interface IOutgoingRequests {
	assetDbId?: string;
}

const OutgoingRequests = ({
	assetDbId,
}: IOutgoingRequests) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [isStopRentingAssetModalOpen, setIsStopRentingAssetModalOpen] = useState(false);
	const [selectedAssetEndDate, setSelectedAssetEndDate] = useState<number | null>(null);
	const [selectedRequestId, setSelectedRequestId] = useState<number | string>('');

	const openStopRentingAssetModal = useCallback(() => setIsStopRentingAssetModalOpen(true), []);

	const closeStopRentingAssetModal = () => setIsStopRentingAssetModalOpen(false);

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		startRentDate,
		endRentDate,
	} = useAppSelector(assetProfileOutgoingRequests);

	const {
		outgoingRequests,
		isLoadingOutgoingRequests: isLoading,
		countOfRecords,
	} = useQueryAssetProfileOutgoingRequests(assetDbId);

	const {
		handleStopRentingAsset,
		isLoadingStopRentingAsset,
	} = useMutateStopRentingAsset();

	const isLoadingOutgoingRequests = isLoading || isLoadingStopRentingAsset;

	const columns = useMemo(() => getColumns({
		openStopRentingAssetModal,
		setSelectedAssetEndDate,
		setSelectedRequestId,
		navigate,
	}), [
		openStopRentingAssetModal,
		setSelectedAssetEndDate,
		navigate,
	]);

	const startDate = useMemo(() => startRentDate ? new Date(startRentDate) : null, [startRentDate]);
	const endDate = useMemo(() => endRentDate ? new Date(endRentDate) : null, [endRentDate]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			placeholder: 'Search by request ID',
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const onChangeStartRentDate = useCallback((value: number | null) => {
		dispatch(setStartRentDate({ startRentDate: value }));
	}, [dispatch]);

	const onChangeEndRentDate = useCallback((value: number | null) => {
		dispatch(setEndRentDate({ endRentDate: value }));
	}, [dispatch]);

	const dateFilters: Array<ITableDatepickerFilter> = useMemo(() => (
		[
			{
				name: 'startRentDate',
				label: 'Start Rent Date',
				handleChange: onChangeStartRentDate,
				selected: startDate,
			},
			{
				name: 'endRentDate',
				label: 'End Rent Date',
				handleChange: onChangeEndRentDate,
				selected: endDate,
			},
		]
	), [
		onChangeStartRentDate,
		onChangeEndRentDate,
		startDate,
		endDate,
	]);

	return (
		<div>
			{selectedRequestId && (
				<Modal
					isOpen={isStopRentingAssetModalOpen && !!selectedRequestId}
					onClose={closeStopRentingAssetModal}
				>
					<StopRentingAssetForm
						actualEndRentDate={selectedAssetEndDate}
						closeModal={closeStopRentingAssetModal}
						handleStopRentingAsset={handleStopRentingAsset}
						itemId={selectedRequestId}
					/>
				</Modal>
			)}
			<Table
				columns={columns}
				data={outgoingRequests}
				dateFilters={dateFilters}
				isLoading={isLoadingOutgoingRequests}
				isWithoutCheckbox
				isWithoutDeleteAction
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
			/>
		</div>
	);
};

export default memo(OutgoingRequests);
